import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar, Clock, Filter, Download, Plus,
  CheckCircle2, XCircle, Clock3, AlertTriangle,
  Calendar as CalendarIcon, User, FileText
} from 'lucide-react';
import { employeeData } from '../data/mockData';

const LeaveRequestCard = ({ request }) => {
  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700'
  };

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      className="bg-white rounded-xl p-6 shadow-sm"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
            <span className="text-lg font-semibold text-indigo-600">
              {request.employee.split(' ').map(n => n[0]).join('')}
            </span>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{request.employee}</h3>
            <p className="text-sm text-gray-500">{request.type}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[request.status]}`}>
          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-500">Start Date</p>
          <p className="text-sm font-medium">{request.startDate}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Duration</p>
          <p className="text-sm font-medium">{request.duration}</p>
        </div>
      </div>

      <div className="flex gap-2 pt-4 border-t">
        {request.status === 'pending' && (
          <>
            <button className="flex-1 py-2 text-green-600 bg-green-50 rounded-lg hover:bg-green-100 
              transition-colors text-sm font-medium">
              Approve
            </button>
            <button className="flex-1 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 
              transition-colors text-sm font-medium">
              Reject
            </button>
          </>
        )}
        <button className="flex-1 py-2 text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 
          transition-colors text-sm font-medium">
          View Details
        </button>
      </div>
    </motion.div>
  );
};

const LeaveBalanceCard = ({ leaveType, used, total }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className="bg-white rounded-xl p-6 shadow-sm"
  >
    <div className="flex justify-between items-start mb-4">
      <div>
        <h3 className="font-semibold text-gray-900">{leaveType}</h3>
        <p className="text-2xl font-bold text-gray-800 mt-1">
          {total - used} <span className="text-sm text-gray-500">days remaining</span>
        </p>
      </div>
      <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
        <Calendar className="w-6 h-6 text-indigo-600" />
      </div>
    </div>
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div 
        className="h-full bg-indigo-600"
        style={{ width: `${(used / total) * 100}%` }}
      />
    </div>
    <div className="flex justify-between mt-2 text-sm">
      <span className="text-gray-500">{used} used</span>
      <span className="text-gray-500">{total} total</span>
    </div>
  </motion.div>
);

const LeaveModule = ({ isMobile }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const leaveBalances = [
    { type: 'Annual Leave', used: 15, total: 30 },
    { type: 'Sick Leave', used: 3, total: 15 },
    { type: 'Personal Leave', used: 2, total: 5 },
    { type: 'Emergency Leave', used: 1, total: 3 }
  ];

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-4">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Types</option>
            <option value="annual">Annual Leave</option>
            <option value="sick">Sick Leave</option>
            <option value="personal">Personal Leave</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 
            flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </button>
          <button className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 
            flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Request
          </button>
        </div>
      </div>

      {/* Leave Balances */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {leaveBalances.map((balance, index) => (
          <LeaveBalanceCard key={index} {...balance} />
        ))}
      </div>

      {/* Leave Requests */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {employeeData.leaveRequests.map((request) => (
          <LeaveRequestCard key={request.id} request={request} />
        ))}
      </div>
    </div>
  );
};

export default LeaveModule; 